/**
 * AuthGate — the boot guard at the root of authenticated routes. On cold start
 * it attempts a single silent refresh; on success it renders the protected
 * shell and, if a deep link was stashed before a prior login redirect, navigates
 * there. On failure it stashes the current path and redirects the browser to
 * centralized login. When an in-memory session already exists (e.g. just
 * returned from `/auth/return`), it renders the shell immediately.
 *
 * Mounting the gate at the root of the protected routes means a deep-link load
 * lands the user back where they wanted to go after sign-in — and it keeps the
 * boot-refresh off the public `/auth/*` routes, which must render without a
 * session.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useCopy } from '@/hooks/useCopy';
import { bootRefresh, popDeepLink, redirectToCentralizedLogin } from './silentRefresh';
import { getAccessToken } from './authStore';
import { useAuth } from './AuthContext';

type GateState = 'booting' | 'authed' | 'redirecting';

export function AuthGate(): ReactNode {
  const { t } = useCopy();
  const location = useLocation();
  const navigate = useNavigate();
  const { setAuthed } = useAuth();
  const [state, setState] = useState<GateState>(() => (getAccessToken() ? 'authed' : 'booting'));
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    if (getAccessToken()) {
      setState('authed');
      return;
    }

    void (async () => {
      const ok = await bootRefresh();
      if (ok) {
        const token = getAccessToken();
        if (token) setAuthed(token, null);
        setState('authed');
        // Restore a stashed deep link (set before the centralized-login round-trip).
        const deep = popDeepLink();
        if (deep && deep !== location.pathname + location.search) {
          navigate(deep, { replace: true });
        }
      } else {
        setState('redirecting');
        redirectToCentralizedLogin(location.pathname + location.search);
      }
    })();
  }, [location.pathname, location.search, navigate, setAuthed]);

  if (state === 'authed') {
    return <Outlet />;
  }

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        gap: '0.5rem',
      }}
    >
      <p>
        {state === 'redirecting'
          ? t('auth.redirecting', 'Redirecting to sign in…')
          : t('app.loading', 'Loading…')}
      </p>
    </div>
  );
}
